import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// ─── Prevent browser scroll restoration ──────────────────────────────────────
if (typeof window !== 'undefined') {
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
  window.scrollTo(0, 0);
  window.addEventListener('beforeunload', () => window.scrollTo(0, 0));
}

// ─── Pages ────────────────────────────────────────────────────────────────────
import Home from './pages/Home';
import BookingPortal from './components/BookingPortal';
import SubscriptionPortal from './pages/SubscriptionPortal';
import CheckoutSim from './pages/CheckoutSim';
import AdminDashboard from './pages/AdminDashboard';

// ─── Shared components ────────────────────────────────────────────────────────
import Navbar from './components/Navbar';
import preloaderLogo from './assets/images/logo.png';

/* ── Loader ── */
function Loader({ onComplete }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    window.scrollTo(0, 0);

    const t1 = setTimeout(() => {
      document.getElementById('loader')?.classList.add('fade-out');
    }, 2400);
    const t2 = setTimeout(() => {
      document.body.style.overflow = '';
      window.scrollTo(0, 0);
      onComplete();
    }, 3000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      document.body.style.overflow = '';
    };
  }, [onComplete]);

  return (
    <div id="loader">
      <img src={preloaderLogo} alt="Aarohan Studio — Music is the Spirit" className="loader-logo" />
      <div className="loader-eq">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="loader-bar" style={{ animationDelay: `${i * 0.05}s` }} />
        ))}
      </div>
    </div>
  );
}

/* ── Page Wipe Transition ── */
function PageWipe() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ scaleX: 1 }}
        animate={{ scaleX: 0 }}
        exit={{ scaleX: 1 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'fixed', inset: 0,
          background: 'linear-gradient(90deg, #100C06 0%, #1A1209 100%)',
          zIndex: 9990,
          transformOrigin: 'right',
          pointerEvents: 'none',
        }}
      />
    </AnimatePresence>
  );
}

/* ── Custom Cursor ── */
function CustomCursor() {
  useEffect(() => {
    const dot   = document.getElementById('cursor-dot');
    const ring  = document.getElementById('cursor-ring');
    const label = document.getElementById('cursor-label');
    let raf;
    let tx = 0, ty = 0;
    let dx = 0, dy = 0;
    let rx = 0, ry = 0;
    let idleTimer;

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
      if (dot)  dot.style.opacity  = '1';
      if (ring) ring.style.opacity = '1';

      const target = e.target.closest('[data-cursor]');
      if (target && label) {
        document.body.classList.add('cursor-hover');
        label.innerText = target.getAttribute('data-cursor');
      } else if (label) {
        document.body.classList.remove('cursor-hover');
        label.innerText = '';
      }

      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (dot)  dot.style.opacity  = '0';
        if (ring) ring.style.opacity = '0';
      }, 3000);
    };

    window.addEventListener('mousemove', onMove);

    const animate = () => {
      dx += (tx - dx) * 0.25;
      dy += (ty - dy) * 0.25;
      rx += (tx - rx) * 0.12;
      ry += (ty - ry) * 0.12;

      if (dot)   dot.style.transform   = `translate(${dx}px, ${dy}px) translate(-50%, -50%)`;
      if (label) label.style.transform = `translate(${dx}px, ${dy}px) translate(-50%, -50%)`;
      if (ring)  ring.style.transform  = `translate(${rx}px, ${ry}px) translate(-50%, -50%)`;

      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(idleTimer);
      window.removeEventListener('mousemove', onMove);
    };
  }, []);

  return null;
}

/* ── Main Home wrapper (with Lenis) ── */
function HomeWrapper() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Start Lenis AFTER loader finishes so DOM is ready
  useEffect(() => {
    if (loading) return;

    window.scrollTo(0, 0);

    const lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    const bar = document.getElementById('scroll-progress');
    lenis.on('scroll', ({ progress }) => {
      if (bar) bar.style.width = `${progress * 100}%`;
    });

    // Sync GSAP ScrollTrigger with Lenis
    lenis.on('scroll', ScrollTrigger.update);
    const lenisRaf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(lenisRaf);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(lenisRaf);
      lenis.destroy();
    };
  }, [loading]);

  const handleComplete = useCallback(() => setLoading(false), []);

  if (loading) {
    return <Loader onComplete={handleComplete} />;
  }

  return (
    <div style={{ opacity: 0, animation: 'loaderFadeIn 1.5s cubic-bezier(0.16,1,0.3,1) forwards' }}>
      <Navbar />
      <PageWipe />
      <Home />
    </div>
  );
}

/* ── Sub-page wrapper (lighter — no loader, no Lenis on these) ── */
function SubPageWrapper({ children }) {
  return (
    <>
      <PageWipe />
      {children}
    </>
  );
}

/* ── Admin password gate ── */
const ADMIN_SESSION_KEY = 'aarohan_admin_auth';

function RequireAdmin({ children }) {
  const [authed, setAuthed] = useState(
    () => sessionStorage.getItem(ADMIN_SESSION_KEY) === '1'
  );
  const [input, setInput]   = useState('');
  const [error, setError]   = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === process.env.REACT_APP_ADMIN_KEY) {
      sessionStorage.setItem(ADMIN_SESSION_KEY, '1');
      setAuthed(true);
    } else {
      setError(true);
      setInput('');
    }
  };

  if (authed) return children;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #100C06 0%, #1A1209 100%)',
    }}>
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          padding: '2.5rem 3rem',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(212,175,55,0.25)',
          borderRadius: '12px',
          minWidth: '300px',
          textAlign: 'center',
        }}
      >
        <h2 style={{ margin: 0, color: '#D4AF37', fontFamily: 'serif', letterSpacing: '0.08em' }}>
          Admin Access
        </h2>
        <input
          type="password"
          value={input}
          placeholder="Enter password"
          autoFocus
          onChange={(e) => { setInput(e.target.value); setError(false); }}
          style={{
            padding: '0.65rem 1rem',
            background: 'rgba(255,255,255,0.06)',
            border: `1px solid ${error ? '#c0392b' : 'rgba(212,175,55,0.3)'}`,
            borderRadius: '6px',
            color: '#fff',
            fontSize: '1rem',
            outline: 'none',
          }}
        />
        {error && (
          <p style={{ margin: 0, color: '#c0392b', fontSize: '0.85rem' }}>
            Incorrect password.
          </p>
        )}
        <button
          type="submit"
          style={{
            padding: '0.65rem',
            background: '#D4AF37',
            color: '#100C06',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 700,
            fontSize: '0.95rem',
            cursor: 'pointer',
            letterSpacing: '0.05em',
          }}
        >
          Enter
        </button>
      </form>
    </div>
  );
}

/* ── Root App ── */
export default function App() {
  return (
    <BrowserRouter>
      {/* Global UI — always mounted */}
      <CustomCursor />
      <div id="scroll-progress" />
      <div id="cursor-dot" />
      <div id="cursor-ring" />
      <div id="cursor-label" />

      <Routes>
        {/* Home — has loader + Lenis */}
        <Route path="/" element={<HomeWrapper />} />

        {/* Sub-pages — have their own Navbar + Lenis inside their own file */}
        <Route path="/book"      element={<SubPageWrapper><BookingPortal /></SubPageWrapper>} />
        <Route path="/subscribe" element={<SubPageWrapper><SubscriptionPortal /></SubPageWrapper>} />
        <Route path="/checkout"  element={<SubPageWrapper><CheckoutSim /></SubPageWrapper>} />
        <Route path="/admin"     element={<RequireAdmin><SubPageWrapper><AdminDashboard /></SubPageWrapper></RequireAdmin>} />

        {/* Catch-all → Home */}
        <Route path="*" element={<HomeWrapper />} />
      </Routes>
    </BrowserRouter>
  );
}
